/**
 * Carrying a visitor from an in-app browser (Instagram / Facebook) to
 * Safari / Chrome without losing what they made.
 *
 * Google and Apple sign-in don't work inside those webviews, and the
 * webview's storage is its own: a plain "open in Safari" lands on a blank
 * canvas. So the handoff URL carries
 *
 *   - the room itself (`fs_room=` — the design, deflated + base64url;
 *     skipped when it would make the URL unreasonably long),
 *   - the first run's progress (`fs_ob=` feng shui, `r3d_ob=` 3D), so its
 *     guide picks up where it was,
 *   - what to do on arrival (`fs_resume=save` reopens the save prompt;
 *     `fs_resume=example` plays the 3D showcase, for a visitor whose
 *     webview couldn't run 3D),
 *   - the ad's attribution (UTMs and `fbclid`, so the sign-up in Safari
 *     still joins the click in Meta and the first touch in GA4).
 *
 * `consumeHandoffParams()` runs once at boot (before the landing gate) and
 * puts the room back on the canvas.
 */

import { getFirstTouch, getLastTouch } from "./attribution";
import {
  getFengShuiOnboarding,
  restoreFengShuiOnboarding,
  type FengShuiOnboardingState,
} from "./fengShuiOnboarding";
import { FLOW_FENG_SHUI, FLOW_PARAM, FLOW_RENDER_3D } from "./landingFlow";
import { seedRender3dRoom } from "./demoRooms";
import {
  getRender3dOnboarding,
  isRender3dLandingPending,
  openRender3dExample,
  restoreRender3dOnboarding,
  type Render3dOnboardingState,
} from "./render3dOnboarding";
import { requestSavePrompt } from "./savePromptRequests";
import { isUnstartedRoom, useDesign } from "./store";
import { addBreadcrumb } from "./telemetry";
import type { Design } from "./types";

export const HANDOFF_ROOM_PARAM = "fs_room";
export const HANDOFF_RESUME_PARAM = "fs_resume";
/** The feng shui first run's progress, so the guide picks up where it was. */
export const HANDOFF_ONBOARDING_PARAM = "fs_ob";
/** The 3D first run's progress. */
export const HANDOFF_RENDER3D_PARAM = "r3d_ob";

export type HandoffResume = "save" | "example";

/** Keep the whole URL comfortably under what Android intents / iOS accept. */
const MAX_ROOM_PARAM_CHARS = 6000;

type HandoffDesign = Pick<Design, "room" | "furniture" | "mounts" | "openings" | "unit">;

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deflate(text: string): Promise<Uint8Array | null> {
  if (typeof CompressionStream === "undefined") return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream("deflate-raw"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function inflate(bytes: Uint8Array): Promise<string | null> {
  if (typeof DecompressionStream === "undefined") return null;
  try {
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    return await new Response(stream).text();
  } catch {
    return null;
  }
}

/** `z.<deflate+b64url>` when the browser can compress, else `j.<b64url json>`. */
export async function encodeHandoffRoom(design: Design): Promise<string | null> {
  const slim: HandoffDesign = {
    room: design.room,
    furniture: design.furniture,
    mounts: design.mounts,
    openings: design.openings,
    unit: design.unit,
  };
  const json = JSON.stringify(slim);
  const packed = await deflate(json);
  const encoded = packed ? `z.${toBase64Url(packed)}` : `j.${toBase64Url(new TextEncoder().encode(json))}`;
  return encoded.length <= MAX_ROOM_PARAM_CHARS ? encoded : null;
}

export async function decodeHandoffRoom(value: string): Promise<HandoffDesign | null> {
  try {
    const [kind, payload] = value.split(".", 2);
    if (!payload) return null;
    const bytes = fromBase64Url(payload);
    const json = kind === "z" ? await inflate(bytes) : new TextDecoder().decode(bytes);
    if (!json) return null;
    const parsed = JSON.parse(json) as Partial<HandoffDesign>;
    if (!parsed || typeof parsed !== "object" || !parsed.room || !Array.isArray(parsed.furniture)) return null;
    return {
      room: parsed.room,
      furniture: parsed.furniture,
      mounts: Array.isArray(parsed.mounts) ? parsed.mounts : [],
      openings: Array.isArray(parsed.openings) ? parsed.openings : [],
      unit: parsed.unit === "metric" ? "metric" : "imperial",
    };
  } catch {
    return null;
  }
}

/** The first run a handoff belongs to, for the `flow` param. */
function activeFlowParam(): typeof FLOW_FENG_SHUI | typeof FLOW_RENDER_3D | undefined {
  if (getRender3dOnboarding().active) return FLOW_RENDER_3D;
  if (getFengShuiOnboarding().active) return FLOW_FENG_SHUI;
  return undefined;
}

const encodeJson = (value: unknown) => toBase64Url(new TextEncoder().encode(JSON.stringify(value)));

/**
 * The URL to open in the system browser: this page, plus the room, the
 * first run's progress, the resume action, the campaign UTMs and the Meta
 * click id. `flow` defaults to the first run that's active.
 */
export async function buildSystemBrowserHandoffUrl(opts: {
  resume?: HandoffResume;
  flow?: typeof FLOW_FENG_SHUI | typeof FLOW_RENDER_3D;
} = {}): Promise<string> {
  const url = new URL(window.location.href);
  url.searchParams.delete(HANDOFF_ROOM_PARAM);
  url.searchParams.delete(HANDOFF_RESUME_PARAM);
  url.searchParams.delete(HANDOFF_ONBOARDING_PARAM);
  url.searchParams.delete(HANDOFF_RENDER3D_PARAM);
  const flow = opts.flow ?? activeFlowParam();
  if (flow) url.searchParams.set(FLOW_PARAM, flow);
  if (opts.resume) url.searchParams.set(HANDOFF_RESUME_PARAM, opts.resume);
  const touch = getLastTouch() ?? getFirstTouch();
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"] as const) {
    const v = touch?.[key];
    if (v && !url.searchParams.has(key)) url.searchParams.set(key, v);
  }
  const design = useDesign.getState().design;
  if (!isUnstartedRoom(design.room)) {
    const encoded = await encodeHandoffRoom(design);
    if (encoded) url.searchParams.set(HANDOFF_ROOM_PARAM, encoded);
  }
  const ob = getFengShuiOnboarding();
  if (ob.active) {
    const carried: Partial<FengShuiOnboardingState> = {
      startedAt: ob.startedAt,
      utmContent: ob.utmContent,
      ownRoomStartedAt: ob.ownRoomStartedAt,
      steps: ob.steps,
    };
    url.searchParams.set(HANDOFF_ONBOARDING_PARAM, encodeJson(carried));
  }
  const r3d = getRender3dOnboarding();
  if (r3d.active) {
    const carried: Partial<Render3dOnboardingState> = {
      startedAt: r3d.startedAt,
      utmContent: r3d.utmContent,
      introSeenAt: r3d.introSeenAt,
      landingDoneAt: r3d.landingDoneAt,
      ownRoomStartedAt: r3d.ownRoomStartedAt,
      steps: r3d.steps,
    };
    url.searchParams.set(HANDOFF_RENDER3D_PARAM, encodeJson(carried));
  }
  return url.toString();
}

function decodeJson<T>(param: string): T | null {
  try {
    return JSON.parse(new TextDecoder().decode(fromBase64Url(param))) as T;
  } catch {
    return null;
  }
}

/**
 * Boot: if the URL carries a handed-off room, put it on the canvas (unless
 * this browser already has a started room that isn't a starter) and strip
 * the params. `fs_resume=save` reopens the save prompt once the app is up.
 * Returns what it did, for the landing gate and tests.
 */
export async function consumeHandoffParams(): Promise<{ room: boolean; resume: HandoffResume | null }> {
  if (typeof window === "undefined") return { room: false, resume: null };
  const url = new URL(window.location.href);
  const roomParam = url.searchParams.get(HANDOFF_ROOM_PARAM);
  const obParam = url.searchParams.get(HANDOFF_ONBOARDING_PARAM);
  const r3dParam = url.searchParams.get(HANDOFF_RENDER3D_PARAM);
  const resumeParam = url.searchParams.get(HANDOFF_RESUME_PARAM);
  const resume: HandoffResume | null = resumeParam === "save" || resumeParam === "example" ? resumeParam : null;
  if (!roomParam && !resume && !obParam && !r3dParam) return { room: false, resume: null };

  // Malformed progress: the landing rule decides as usual.
  const carriedFengShui = obParam ? decodeJson<Partial<FengShuiOnboardingState>>(obParam) : null;
  if (carriedFengShui) restoreFengShuiOnboarding(carriedFengShui);
  const carried3d = r3dParam ? decodeJson<Partial<Render3dOnboardingState>>(r3dParam) : null;
  if (carried3d) restoreRender3dOnboarding(carried3d);

  let loaded = false;
  if (roomParam) {
    const slim = await decodeHandoffRoom(roomParam);
    const current = useDesign.getState().design;
    const canReplace = isUnstartedRoom(current.room) || /^starter_/.test(current.room.id);
    if (slim && canReplace) {
      useDesign.getState().loadDesign({
        ...current,
        ...slim,
        consultations: [],
        rankings: [],
      });
      loaded = true;
      addBreadcrumb("handoff:room-loaded", { id: slim.room.id });
    }
  }
  // A 3D visitor still on the landing whose room didn't fit in the URL gets the showcase room back.
  if (carried3d && !loaded && isRender3dLandingPending() && isUnstartedRoom(useDesign.getState().design.room)) {
    seedRender3dRoom(getRender3dOnboarding().utmContent);
  }
  url.searchParams.delete(HANDOFF_ROOM_PARAM);
  url.searchParams.delete(HANDOFF_RESUME_PARAM);
  url.searchParams.delete(HANDOFF_ONBOARDING_PARAM);
  url.searchParams.delete(HANDOFF_RENDER3D_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
  // Let the tree mount first; the sheet and the overlay subscribe on mount.
  if (resume === "save") {
    const surface = getRender3dOnboarding().active ? "render_3d_guide" : "feng_shui_guide";
    setTimeout(() => requestSavePrompt({ surface }), 600);
  } else if (resume === "example") {
    setTimeout(() => openRender3dExample("handoff"), 600);
  }
  return { room: loaded, resume };
}
